import { useEffect, useRef, useState } from "react"

interface UseCountdownTimerOptions {
  startMinutes: number
  onComplete?: () => void
}

export default function useCountdownTimer({
  startMinutes,
  onComplete,
}: UseCountdownTimerOptions) {
  const [timeLeft, setTimeLeft] = useState(
    `${startMinutes.toString().padStart(2, "0")}:00`,
  )

  // Store endTime in a ref so it persists across re-renders and doesn't reset
  const endTimeRef = useRef<number | null>(null)
  const onCompleteRef = useRef(onComplete)

  // Keep onComplete ref updated without triggering effect re-runs
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    // Only calculate endTime once on initial mount
    if (endTimeRef.current === null) {
      endTimeRef.current = new Date().getTime() + startMinutes * 60 * 1000
    }

    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = endTimeRef.current! - now

      if (distance <= 0) {
        clearInterval(timer)
        setTimeLeft("00:00")
        onCompleteRef.current?.()
        return
      }

      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setTimeLeft(
        `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      )
    }, 1000)

    return () => clearInterval(timer)
  }, [startMinutes])

  return timeLeft
}
